/**
 * usePushNotifications.js — Phase B.3
 *
 * Polls every 5 minutes for trigger events and fires browser notifications.
 * Uses localStorage to debounce: the same notification won't fire again within 4h.
 *
 * Trigger sources (spec v5):
 *   1. Demo starting in 25–35 minutes
 *   2. Trial health = Red (checked each poll)
 *   3. Gmail sync stale > 26h
 *   4. BigDough sync stale > 24h (weekday only)
 *
 * Props:
 *   data       — live sheet data from useSheetData (kept as ref so it doesn't
 *                re-create the polling interval on every data refresh)
 *   onNavigate — (tabId: string) => void — focuses window + changes tab
 */

import { useEffect, useRef, useCallback } from 'react';
import { trialLastLogin } from '../utils/health.js';

const POLL_INTERVAL_MS = 5 * 60 * 1000;  // 5 minutes
const DEBOUNCE_MS      = 4 * 60 * 60 * 1000; // 4 hours

// ── Debounce helpers ──────────────────────────────────────────────────────────

function _loadStore() {
  try { return JSON.parse(localStorage.getItem('push_debounce') ?? '{}'); }
  catch { return {}; }
}

function _saveStore(store) {
  try { localStorage.setItem('push_debounce', JSON.stringify(store)); }
  catch { /* ignore quota errors */ }
}

function _isDebounced(tag) {
  const store = _loadStore();
  return (store[tag] ?? 0) + DEBOUNCE_MS > Date.now();
}

function _markDebounced(tag) {
  const store = _loadStore();
  store[tag] = Date.now();
  // Prune entries older than 24h to keep localStorage from growing
  const cutoff = Date.now() - 24 * 3_600_000;
  for (const k of Object.keys(store)) {
    if (store[k] < cutoff) delete store[k];
  }
  _saveStore(store);
}

// ── Notification helper ───────────────────────────────────────────────────────

function _fire(title, body, tag, onClick) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (_isDebounced(tag)) return;
  _markDebounced(tag);
  try {
    const n = new Notification(title, { body, tag, icon: '/favicon.svg' });
    if (onClick) n.onclick = onClick;
  } catch (e) {
    console.warn('[push] Notification failed:', e);
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function usePushNotifications({ data, onNavigate }) {
  const dataRef     = useRef(data);
  const navigateRef = useRef(onNavigate);

  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { navigateRef.current = onNavigate; }, [onNavigate]);

  // Request permission on first mount
  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      try { Notification.requestPermission().then(() => {}).catch(() => {}); } catch {}
    }
  }, []);

  // Polling loop
  const poll = useCallback(() => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const d = dataRef.current;
    if (!d) return;

    const { demos = [], trials = [], config = {} } = d;
    const now = new Date();
    const navigate = navigateRef.current;

    // 1a. Demos today — morning alert (8–10am, once per day)
    const hour = now.getHours();
    if (hour >= 8 && hour < 10) {
      const todayStr = now.toISOString().slice(0, 10);
      const todayDemos = demos.filter(
        (d) => (d.status ?? '').toLowerCase() === 'scheduled' && (d.scheduled_date ?? '').slice(0, 10) === todayStr
      );
      if (todayDemos.length > 0) {
        _fire(
          `${todayDemos.length} demo${todayDemos.length > 1 ? 's' : ''} today`,
          todayDemos.map((d) => d.event_title || 'Demo').join(', '),
          `demos_today_${todayStr}`,
          () => { window.focus(); navigate?.('inprogress'); }
        );
      }
    }

    // 1b. Demos needing outcome logged
    const todayMs = (() => { const d = new Date(); d.setHours(0,0,0,0); return d.getTime(); })();
    const needsOutcome = demos.filter((d) => {
      const st = (d.status ?? '').toLowerCase();
      if ((d.next_step ?? '').toLowerCase() === 'dead') return false;
      if (st === 'completed' && !String(d.outcome ?? '').trim()) return true;
      if (st === 'scheduled') {
        const raw = d.scheduled_date ? new Date(d.scheduled_date) : null;
        const dayMs = raw ? new Date(raw.getFullYear(), raw.getMonth(), raw.getDate()).getTime() : null;
        return dayMs !== null && dayMs < todayMs;
      }
      return false;
    });
    if (needsOutcome.length > 0) {
      _fire(
        `${needsOutcome.length} demo${needsOutcome.length > 1 ? 's' : ''} need outcome logged`,
        'Open In Progress to log results',
        'demos_needs_outcome',
        () => { window.focus(); navigate?.('inprogress'); }
      );
    }

    // 1c. Demo starting in ~30 minutes
    for (const demo of demos) {
      if ((demo.status ?? '').toLowerCase() !== 'scheduled') continue;
      const demoTime = new Date(demo.scheduled_date ?? '');
      if (isNaN(demoTime)) continue;
      const minsUntil = (demoTime.getTime() - now.getTime()) / 60_000;
      if (minsUntil >= 25 && minsUntil <= 35) {
        _fire(
          'Demo in 30 minutes',
          demo.event_title ?? 'Meeting starting soon',
          `demo_soon_${demo.demo_id}`,
          () => { window.focus(); navigate?.('inprogress'); }
        );
      }
    }

    // 2. Active trial health = Red (login > 7 days)
    for (const trial of trials) {
      if ((trial.status ?? '').toLowerCase() !== 'active') continue;
      const lastLoginIso = trialLastLogin(trial);
      const lastLogin = lastLoginIso ? new Date(lastLoginIso) : null;
      const loginDays = lastLogin ? (now - lastLogin) / 86_400_000 : Infinity;
      if (loginDays >= 8) {
        _fire(
          'Trial health: Churning',
          `${trial.contact_id} — ${Math.round(loginDays)} days without a login`,
          `trial_red_${trial.trial_id}`,
          () => { window.focus(); navigate?.('inprogress'); }
        );
      }
    }

    // 3. Gmail sync stale (> 26h, weekday only)
    const isWeekday = now.getDay() !== 0 && now.getDay() !== 6;
    const lastGmail = config['last_gmail_sync'] ?? '';
    if (isWeekday && lastGmail) {
      const hoursAgo = (now - new Date(lastGmail)) / 3_600_000;
      if (hoursAgo > 26) {
        _fire(
          'Gmail sync is stale',
          `Last synced ${Math.round(hoursAgo)}h ago — check Apps Script`,
          'gmail_stale',
          () => window.focus()
        );
      }
    }

    // 4. BigDough sync stale (> 24h, weekday only, after 9am)
    const isAfter9am = now.getHours() >= 9;
    const lastBD = config['last_bigdough_sync'] ?? '';
    if (isWeekday && isAfter9am && lastBD) {
      const hoursAgo = (now - new Date(lastBD)) / 3_600_000;
      if (hoursAgo > 24) {
        _fire(
          'BigDough not synced today',
          'Upload this morning\'s export to keep Last Touch accurate',
          'bd_stale',
          () => { window.focus(); navigate?.('sync'); }
        );
      }
    }
  }, []);

  useEffect(() => {
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [poll]);
}
